import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { compact } from 'lodash';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { NotFound } from '../../../../components/NotFound';
import { SomethingWentWrongWithReport } from '../../../../components/SomethingWentWrong';
import {
  BlockCheck,
  useBlockOnRouteChange
} from '../../../../hooks/useBlockOnRouteChange';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { useQueryParam, useRoutes, useStringQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useExperienceTemplateById } from '../../../../services/experiences/template';
import {
  normalizeAutomationActions,
  normalizeCreateMeetingBlock,
  normalizeDocuSignEnvelopeSignBlock,
  normalizeSubtaskLabelField,
  normalizeSwagUpChoiceBlock,
  normalizeTaskBlockHooks,
  validateEntireTemplate
} from '../../../../services/experiences/templateValidation';
import { updateDoc } from '../../../../services/firebase';
import { hasCurrentUserExperienceLevelScopes } from '../../../../services/permissions';
import { useAIObservability } from '../../../../services/productAnalytics/aiObservabilityEvents';
import { now } from '../../../../services/time';
import {
  PreLaunchChecklist,
  PreLaunchChecklistBoundary,
  type AttestationKey,
  type Attestations
} from './PreLaunchChecklist';
import { StepBasics } from './StepBasics';
import { StepEmployeeApp } from './StepEmployeeApp';
import { AnchorStash, AnchorStashContext, StepLaunchDate } from './StepLaunchDate';
import { StepParticipants } from './StepParticipants';
import { StepReview } from './StepReview';
import { StepTimeline } from './StepTimeline';
import { Step } from './types';

// Add a stepper
// Keep track of current step in URL (easy to link directly to a step later on)
// Define steps
// Provide a default footer - we still want the flexibility to allow each step to go custom.

const Container = styled('div')`
  flex: 1;
  display: grid;
  grid-template-columns: 150px 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  grid-row-gap: ${(p) => p.theme.spacing(6)}px;
  margin-bottom: ${(p) => p.theme.spacing(5)}px;
`;

const StepContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(6),
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
}));

const ExperienceTitle = styled('span')((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

const Body = <T extends unknown>({
  d,
  step,
  nextStep,
  onChange,
  goTo,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt,
  validationErrors
}: {
  d: T;
  step: Step<T>;
  nextStep?: string;
  onChange: (fn: (prev: T) => T) => void;
  goTo: (step: string) => void;
  onCheckChanges?: () => Promise<void>;
  onLaunchFromRoughDraft?: () => Promise<void>;
  isLaunchReady?: boolean;
  onLaunch?: () => void;
  lastSavedAt?: Date | null;
  validationErrors?: import('../../../../services/experiences/templateValidation').TemplateValidationError[];
}) => {
  return (
    <StepContainer>
      <step.body
        d={d}
        onChange={onChange}
        goTo={goTo}
        label={step.label}
        nextStep={nextStep}
        onCheckChanges={onCheckChanges}
        onLaunchFromRoughDraft={onLaunchFromRoughDraft}
        isLaunchReady={isLaunchReady}
        onLaunch={onLaunch}
        lastSavedAt={lastSavedAt}
        validationErrors={validationErrors}
      />
    </StepContainer>
  );
};

const StyledStep = styled('div')`
  font-size: ${(p) => p.theme.custom.fontSize.m}px;
  margin-bottom: ${(p) => p.theme.spacing(3)}px;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  width: fit-content;
`;

const PastStep = styled(StyledStep)`
  color: ${(p) => p.theme.custom.colors.gray.w400};
`;

const ActiveStep = styled(StyledStep)`
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
  border-color: ${(p) => p.theme.custom.colors.green.w400};
`;

const FutureStep = styled(StyledStep)``;

const Stepper = <T extends unknown>({
  value,
  onChange,
  steps
}: {
  value: Step<T>;
  onChange: (nextStep: Step<T>) => void;
  steps: Step<T>[];
}) => {
  const activeI = steps.indexOf(value);
  return (
    <div>
      {steps.map((s, i) => {
        const Outer =
          i < activeI ? PastStep : i > activeI ? FutureStep : ActiveStep;
        return (
          <Outer key={s.key}>
            <div role="button" onClick={() => onChange(s)}>
              {s.label}
            </div>
          </Outer>
        );
      })}
    </div>
  );
};

const ContentExperienceTemplateEdit = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES, goTo } = useRoutes();
  const { id: updatedBy, org } = useCurrentUser();
  const customUserFields = org.customUserFields ?? [];
  const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();

  const isRoughDraft =
    tpl.data.status === ExperienceTemplateStatus.ROUGH_DRAFT;

  // All steps including Review (needed for navigation after "Check changes & launch")
  const allSteps: Step<Doc<IExperienceTemplate>>[] = useMemo(
    () =>
      compact([
        {
          key: 'basics',
          label: t('Basics'),
          body: StepBasics
        },
        {
          key: 'startAndEnd',
          label: t('Start and End'),
          body: StepLaunchDate
        },
        {
          key: 'participants',
          label: t('Participants'),
          body: StepParticipants
        },
        {
          key: 'tasks',
          label: t('Timeline'),
          body: StepTimeline
        },
        {
          key: 'employeeApp',
          label: t('Experience Hub'),
          body: StepEmployeeApp
        },
        {
          key: 'review',
          label: t('Review and Launch'),
          body: StepReview
        }
      ]),
    [t]
  );

  // Steps visible in the sidebar stepper (hide Review for ROUGH_DRAFT)
  const visibleSteps = useMemo(
    () =>
      isRoughDraft
        ? allSteps.filter((s) => s.key !== 'review')
        : allSteps,
    [allSteps, isRoughDraft]
  );

  const defaultStep = isRoughDraft
    ? allSteps.find((s) => s.key === 'tasks') || allSteps[0]
    : allSteps[0];

  const [step, setStep] = useQueryParam(
    'step',
    (p) => allSteps.find((s) => s.key === p) || defaultStep,
    (s) => s?.key
  );

  // Set by the Copilot build-log "Continue" handover (DadoCopilot/index.tsx).
  // When present, auto-run Check Changes once to hand the user from the
  // builder into the validator, then strip the param.
  const [checkChangesRequested, setCheckChangesRequested] =
    useStringQueryParam('checkChanges', '');

  const [model, setModel] = useState(tpl || null);
  useEffect(() => setModel(tpl || null), [tpl]);

  // Auto-save for ROUGH_DRAFT mode
  // Initialize from Firestore's updatedAt so returning to the page shows "Last saved: Xm ago"
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(() => {
    if (isRoughDraft && tpl?.data?.updatedAt) {
      const ts = tpl.data.updatedAt;
      return typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts as any);
    }
    return null;
  });

  // Keep lastSavedAt in sync when Firestore updates externally (e.g., copilot writes)
  useEffect(() => {
    if (!isRoughDraft || !tpl?.data?.updatedAt) return;
    const ts = tpl.data.updatedAt;
    const fsDate = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts as any);
    // Only update if Firestore timestamp is newer than our local lastSavedAt
    if (!lastSavedAt || fsDate.getTime() > lastSavedAt.getTime()) {
      setLastSavedAt(fsDate);
    }
  }, [tpl?.data?.updatedAt, isRoughDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outlives the launch-date step so its option memory survives step changes.
  const anchorStashRef = useRef<AnchorStash>({});

  // Track whether the user has made local changes not yet in Firestore
  const hasLocalChanges = useRef(false);
  useEffect(() => {
    // model is set from tpl on Firestore updates — reset local changes flag
    hasLocalChanges.current = false;
  }, [tpl]);

  // Auto-save for ROUGH_DRAFT: triggers 2s after user edits (not on Firestore refreshes)
  useEffect(() => {
    if (!isRoughDraft || !model || !hasLocalChanges.current) return;
    const timer = setTimeout(() => {
      updateDoc(model, (x) => ({
        ...x,
        updatedBy,
        updatedAt: now()
      }))
        .then(() => {
          setLastSavedAt(new Date());
          hasLocalChanges.current = false;
        })
        .catch((err) => {
          console.error('Auto-save failed:', err);
        });
    }, 2000);
    return () => clearTimeout(timer);
  }, [model, isRoughDraft, updatedBy]);

  const interceptorMessage = t('taskEdit.leaveInterceptor');
  const shouldBlock = useCallback<BlockCheck>(
    (prev, next) => {
      if (isRoughDraft) return; // auto-saved, no blocking
      const pathNameChange = !next || prev?.pathname !== next?.pathname;
      if (pathNameChange && model !== tpl) {
        return interceptorMessage;
      }
    },
    [model, tpl, interceptorMessage, isRoughDraft]
  );
  useBlockOnRouteChange(shouldBlock);

  const aiObs = useAIObservability();

  // Validation errors for guided fix flow
  const [validationErrors, setValidationErrors] = useState<
    import('../../../../services/experiences/templateValidation').TemplateValidationError[]
  >([]);

  // Two-button "Check Changes / Launch" gate (ported from react-19).
  //   1. Check Changes — runs the validator + shows the checklist.
  //   2. Launch — disabled until Check Changes returned zero errors AND
  //      no edits since. editSeq bumps on every user edit (Body onChange);
  //      lastValidatedCleanSeq captures the seq at the last clean check.
  //
  // NOTE: heal-on-launch normalizers are NOT chained here — verifiable's
  // templateValidation lacks them (tracked in new-main-ph8z.10). onCheckChanges
  // validates without healing for now.
  const [editSeq, setEditSeq] = useState(0);
  const [lastValidatedCleanSeq, setLastValidatedCleanSeq] = useState<number | null>(null);
  // The checklist pane can be dismissed (× in its header) to get it out of the
  // way. It re-appears whenever validation re-runs (Check Changes, or Launch's
  // re-validation) so a fresh result is never hidden behind a stale dismiss.
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const isValidatedClean =
    lastValidatedCleanSeq !== null &&
    lastValidatedCleanSeq === editSeq &&
    validationErrors.length === 0;

  // Manual pre-launch attestations (PreLaunchChecklist "Needs review"
  // state) — REQUIRED to launch. In-memory only: any edit drops the clean
  // gate and resets attestations so the admin re-confirms after changes.
  const [attestations, setAttestations] = useState<Attestations>({
    audiences: false,
    sender: false,
    preview: false
  });
  const allAttested =
    attestations.audiences && attestations.sender && attestations.preview;
  const toggleAttestation = useCallback((key: AttestationKey) => {
    setAttestations((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  useEffect(() => {
    if (!isValidatedClean) {
      setAttestations((prev) =>
        prev.audiences || prev.sender || prev.preview
          ? { audiences: false, sender: false, preview: false }
          : prev
      );
    }
  }, [isValidatedClean]);

  // Full launch gate: validated clean AND all manual attestations. Passed
  // down so StandardFooter's Launch respects the attestations too.
  const isLaunchReady = isValidatedClean && allAttested;

  // Open an errored item (task / automation / message). Shared by the
  // Check Changes handler and the checklist's open/next-issue navigation.
  const openValidationItem = useCallback(
    (itemType: string, itemId: string) => {
      const tasksStep = allSteps.find((s) => s.key === 'tasks');
      if (tasksStep && step !== tasksStep) setStep(tasksStep);
      const base = ROUTES.experiences.details.edit.url(model?.id ?? '', { step: 'tasks' });
      const paramKey = itemType === 'TASK' ? 't' : itemType === 'AUTOMATION' ? 'a' : 'm';
      goTo(`${base}&${paramKey}=${itemId}`);
    },
    [allSteps, step, setStep, ROUTES, model, goTo]
  );

  // Live re-validation while the checklist is open: recompute errors when
  // model.data changes so a fix drops its item. Only runs when the
  // checklist is already showing (prev non-empty) — gated via functional
  // setState so we don't validate on every keystroke when no one's watching.
  useEffect(() => {
    if (!model) return;
    setValidationErrors((prev) => {
      if (!prev.length) return prev;
      try {
        return validateEntireTemplate(model.data, t, customUserFields);
      } catch (err) {
        console.warn('[TemplateEdit] live validation threw; keeping previous errors', err);
        return prev;
      }
    });
  }, [model, t, customUserFields]);

  // "Check Changes" for ROUGH_DRAFT: save + validate. Does NOT transition
  // status. On clean, captures editSeq into lastValidatedCleanSeq so Launch
  // enables.
  const onCheckChanges = useCallback(async () => {
    try {
      // Re-running validation always re-reveals the checklist, even if the
      // admin had dismissed it.
      setChecklistDismissed(false);
      const seqAtStart = editSeq;
      aiObs.trackCheckChangesClicked(model.id, { editSeqAtClick: seqAtStart });
      const checkStartedAt = performance.now();
      // Heal-on-launch: each normalizer fires onHealed ONLY when it actually
      // heals something (idempotent passes are silent). Chained inner→outer
      // over the same opts so AI-malformed blocks/actions are repaired before
      // validation + writeback.
      const templateIdForHeal = model.id;
      const healOpts = {
        onHealed: (e: { normalizerName: string; itemsHealed: number }): void => {
          aiObs.trackHealOnLaunchApplied(templateIdForHeal, {
            normalizerName: e.normalizerName,
            itemsHealed: e.itemsHealed,
            trigger: 'check-changes'
          });
        }
      };
      const healed = normalizeAutomationActions(
        normalizeDocuSignEnvelopeSignBlock(
          normalizeSwagUpChoiceBlock(
            normalizeCreateMeetingBlock(
              normalizeSubtaskLabelField(
                normalizeTaskBlockHooks(model.data, healOpts),
                healOpts
              ),
              healOpts
            ),
            healOpts
          ),
          healOpts
        ),
        healOpts
      );
      await updateDoc(model, (x) => ({
        ...x,
        tasks: healed.tasks,
        automations: healed.automations,
        updatedBy,
        updatedAt: now()
      }));
      const errors = validateEntireTemplate(healed, t, customUserFields);
      const durationMs = Math.round(performance.now() - checkStartedAt);
      aiObs.trackCheckChangesResult(model.id, { errorCount: errors.length, durationMs });
      if (errors.length) {
        setValidationErrors(errors);
        setLastValidatedCleanSeq(null);
        const first = errors[0];
        if (first && first.itemType && first.itemId) {
          openValidationItem(first.itemType, first.itemId);
        }
        return;
      }
      setValidationErrors([]);
      setLastValidatedCleanSeq(seqAtStart);
      enqueueSnackbarSuccess(t('Validation passed — Launch is now available.'));
    } catch (err) {
      console.error('[onCheckChanges] failed', err);
      enqueueSnackbarError(
        err instanceof Error
          ? t('Could not validate: {{ message }}', { message: err.message })
          : t('Could not validate this template.')
      );
      setLastValidatedCleanSeq(null);
    }
  }, [model, updatedBy, t, customUserFields, enqueueSnackbarError, enqueueSnackbarSuccess, openValidationItem, editSeq, aiObs]);

  // Copilot "Continue" handover: when DadoCopilot sets ?checkChanges=1,
  // auto-run Check Changes once so the user lands in the validator after a
  // build. Strip the param immediately + ref-guard so re-renders / refreshes
  // don't re-fire. Gated on isRoughDraft (the only mode where onCheckChanges
  // is wired; Copilot always builds templates as ROUGH_DRAFT).
  const handledCheckChangesRef = useRef(false);
  useEffect(() => {
    if (checkChangesRequested !== '1') return;
    if (handledCheckChangesRef.current) return;
    handledCheckChangesRef.current = true;
    setCheckChangesRequested(null);
    if (isRoughDraft) {
      onCheckChanges();
    }
  }, [checkChangesRequested, isRoughDraft, onCheckChanges, setCheckChangesRequested]);

  // "Launch" for ROUGH_DRAFT (after Check Changes returned clean):
  // ROUGH_DRAFT → DRAFT → ACTIVE. Re-validates as a last-line safety net.
  const onLaunchFromRoughDraft = useCallback(async () => {
    if (!model) return;
    try {
      aiObs.trackLaunchClicked(model.id, { isLaunchReady });
      const errors = validateEntireTemplate(model.data, t, customUserFields);
      if (errors.length) {
        aiObs.trackLaunchBlockedByRevalidation(model.id, { errorCount: errors.length });
        setValidationErrors(errors);
        setLastValidatedCleanSeq(null);
        // Don't let a prior dismiss hide launch-blocking errors.
        setChecklistDismissed(false);
        enqueueSnackbarError(
          t('Validation no longer passes. Please re-run Check Changes before launching.')
        );
        const first = errors[0];
        if (first && first.itemType && first.itemId) {
          openValidationItem(first.itemType, first.itemId);
        }
        return;
      }
      await updateDoc(model, (x) => ({
        ...x,
        status: ExperienceTemplateStatus.DRAFT,
        updatedBy,
        updatedAt: now()
      }));
      await updateDoc(model, (x) => ({
        ...x,
        status: ExperienceTemplateStatus.ACTIVE,
        updatedBy,
        updatedAt: now()
      }));
      enqueueSnackbarSuccess(t('Experience launched'));
      goTo(ROUTES.experiences.details.general.url(model.id));
    } catch (err) {
      console.error('[onLaunchFromRoughDraft] failed', err);
      enqueueSnackbarError(
        err instanceof Error
          ? t('Could not launch: {{ message }}', { message: err.message })
          : t('Could not launch this template.')
      );
    }
  }, [model, updatedBy, t, customUserFields, enqueueSnackbarError, enqueueSnackbarSuccess, openValidationItem, goTo, ROUTES, aiObs, isLaunchReady]);

  // "Launch" for DRAFT: go to Review
  const onLaunch = useCallback(() => {
    const reviewStep = allSteps.find((s) => s.key === 'review');
    if (reviewStep) setStep(reviewStep);
  }, [allSteps, setStep]);

  if (tpl && !model) {
    return <Loader />;
  }

  return (
    <PageBody noTopPadding>
      <PageToolbar
        backButton={
          <>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                goTo(ROUTES.experiences.details.general.url(tpl.id))
              }
              startIcon={<ChevronLeft size="20" />}
            >
              {t('Back to overview')}
            </Button>
          </>
        }
      >
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Edit')}
            {(model.data.adminTitle || model.data.title) && (
              <ExperienceTitle>
                {' '}
                {model.data.adminTitle || model.data.title}
              </ExperienceTitle>
            )}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>
      <Container>
        <Stepper value={step} onChange={setStep} steps={visibleSteps} />
        <AnchorStashContext.Provider value={anchorStashRef}>
        <Body
          d={model}
          onChange={((fn: any) => {
            hasLocalChanges.current = true;
            // Bump editSeq so Launch re-disables — admin must re-run Check
            // Changes after any edit. (Firestore-subscription model updates
            // don't go through here, so they don't invalidate readiness.)
            setEditSeq((n) => n + 1);
            setModel(fn);
          }) as any}
          goTo={(key) => {
            const nextStep = allSteps.find((s) => s.key === key);
            if (nextStep) {
              setStep(nextStep);
            }
          }}
          step={step}
          nextStep={undefined}
          onCheckChanges={isRoughDraft ? onCheckChanges : undefined}
          onLaunchFromRoughDraft={isRoughDraft ? onLaunchFromRoughDraft : undefined}
          isLaunchReady={isRoughDraft ? isLaunchReady : undefined}
          onLaunch={
            !isRoughDraft &&
            model.data.status === ExperienceTemplateStatus.DRAFT
              ? onLaunch
              : undefined
          }
          lastSavedAt={isRoughDraft ? lastSavedAt : null}
          validationErrors={validationErrors.length > 0 ? validationErrors : undefined}
        />
        </AnchorStashContext.Provider>
      </Container>
      {/* Pre-launch checklist — single gradient card replacing ValidationGuide.
       * Shows when there are errors (Issues found) OR when validated clean in
       * ROUGH_DRAFT (Needs review / All clear). */}
      {(validationErrors.length > 0 || (isRoughDraft && isValidatedClean)) &&
        !checklistDismissed && (
          <PreLaunchChecklistBoundary>
            <PreLaunchChecklist
              errors={validationErrors}
              attestations={attestations}
              onToggleAttestation={toggleAttestation}
              onOpenItem={openValidationItem}
              templateId={model.id}
              onClose={() => setChecklistDismissed(true)}
            />
          </PreLaunchChecklistBoundary>
        )}
    </PageBody>
  );
};

const PageExperienceTemplateEditPostViewCheck = ({
  tplId
}: {
  tplId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();
  const [tpl, loading, error] = useExperienceTemplateById(tplId);

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'template_edit', tplId }}
      />
    );
  }
  if (!tpl) {
    return <NotFound />;
  }

  if (!hasCurrentUserExperienceLevelScopes(currentUser, tplId, ['edit'])) {
    return (
      <NoAccess
        title={t('You can’t edit “{{expName}}”', {
          expName: tpl.data.adminTitle || tpl.data.title
        })}
        action={t('editing this experience')}
        buttonLabel={t('Go to Experience Overview')}
        buttonLink={ROUTES.experiences.details.general.url(tplId)}
      />
    );
  }

  return <ContentExperienceTemplateEdit tpl={tpl} />;
};

export const PageExperienceTemplateEdit = ({ tplId }: { tplId: string }) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();

  if (!hasCurrentUserExperienceLevelScopes(currentUser, tplId, ['view'])) {
    return (
      <NoAccess
        title={t('You can’t view this experience')}
        action={t('viewing this experience')}
        buttonLabel={t('Go to Experience Overview')}
        buttonLink={ROUTES.experiences.details.general.url(tplId)}
      />
    );
  }
  return <PageExperienceTemplateEditPostViewCheck tplId={tplId} />;
};
